import { useEffect } from 'react';

/**
 * Installs a single scroll listener on `.main-content-scroll` that writes
 * a binary chrome offset to CSS variables on <html>:
 *   --mobile-chrome-offset    : "0px"  | "48px"
 *   --mobile-chrome-progress  : "0"    | "1"
 *
 * Behavior is direction-aware, like iOS Safari's URL bar:
 *   - Scroll DOWN past `SCROLL_TOP_PIN` with >= `DOWN_THRESHOLD` of cumulative
 *     downward delta → hide (offset 48px).
 *   - Scroll UP by >= `UP_THRESHOLD` OR back into the top zone → show (0px).
 *
 * Consumers wrap the resulting transform with a CSS transition so the change
 * eases instead of snapping frame-for-frame with finger travel.
 */
const MAX_OFFSET = 48;
const SCROLL_TOP_PIN = 16;       // always-visible zone
const DEAD_ZONE_DOWN = 6;        // ignore tiny downward jitters
const DEAD_ZONE_UP = 4;          // ignore tiny upward jitters

let listenerCount = 0;
let scrollEl: HTMLElement | null = null;
let rafId: number | null = null;
let observer: MutationObserver | null = null;

let lastY = 0;
let currentOffset = 0;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const apply = (offset: number) => {
  const root = document.documentElement;
  root.style.setProperty('--mobile-chrome-offset', `${offset}px`);
  root.style.setProperty('--mobile-chrome-progress', String(offset / MAX_OFFSET));
};

const setOffset = (next: number) => {
  const clamped = clamp(next, 0, MAX_OFFSET);
  if (Math.abs(clamped - currentOffset) < 0.5) return;
  currentOffset = clamped;
  apply(clamped);
};

const readPinUntil = (el: HTMLElement): number => {
  const raw = el.dataset.chromePinUntil;
  if (!raw) return SCROLL_TOP_PIN;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(SCROLL_TOP_PIN, n) : SCROLL_TOP_PIN;
};

const update = () => {
  rafId = null;
  if (!scrollEl) return;
  const y = scrollEl.scrollTop;
  const dy = y - lastY;
  lastY = y;

  const pinUntil = readPinUntil(scrollEl);

  // Always pinned visible inside the top zone (or until per-page hero clears).
  if (y <= pinUntil) {
    setOffset(0);
    return;
  }

  // Finger-track: hide proportional to downward travel, reveal proportional to upward.
  if (dy > DEAD_ZONE_DOWN) {
    setOffset(currentOffset + (dy - DEAD_ZONE_DOWN));
  } else if (dy < -DEAD_ZONE_UP) {
    setOffset(currentOffset + (dy + DEAD_ZONE_UP));
  }
};

const handleScroll = () => {
  if (rafId !== null) return;
  rafId = window.requestAnimationFrame(update);
};

const bind = (el: HTMLElement) => {
  if (scrollEl === el) return;
  unbind();
  scrollEl = el;
  lastY = el.scrollTop;
  el.addEventListener('scroll', handleScroll, { passive: true });
  // Initial state — pinned visible unless the page is already scrolled deep.
  setOffset(el.scrollTop > SCROLL_TOP_PIN + MAX_OFFSET ? MAX_OFFSET : 0);
};

const unbind = () => {
  if (scrollEl) {
    scrollEl.removeEventListener('scroll', handleScroll);
    scrollEl = null;
  }
  if (rafId !== null) {
    window.cancelAnimationFrame(rafId);
    rafId = null;
  }
};

const tryAttach = () => {
  const el = document.querySelector('.main-content-scroll') as HTMLElement | null;
  if (el) bind(el);
};

const startObserving = () => {
  if (observer) return;
  observer = new MutationObserver(() => {
    if (scrollEl && !document.contains(scrollEl)) {
      unbind();
    }
    if (!scrollEl) tryAttach();
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

const stopObserving = () => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
};

export const useMobileChromeOffsetTracker = (): void => {
  useEffect(() => {
    listenerCount++;
    if (listenerCount === 1) {
      tryAttach();
      startObserving();
    }
    return () => {
      listenerCount--;
      if (listenerCount === 0) {
        unbind();
        stopObserving();
        currentOffset = 0;
        apply(0);
      }
    };
  }, []);
};

/**
 * Back-compat shim. Returns 0 and only installs the global tracker so
 * existing consumers keep working without forcing per-frame React renders.
 * Prefer reading `var(--mobile-chrome-offset)` directly in styles.
 */
export const useMobileChromeOffset = (): number => {
  useMobileChromeOffsetTracker();
  return 0;
};
